import { Box, makeStyles } from '@material-ui/core';
import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';

import { OptionListItemContainer } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/OptionListItemContainer';
import { OrchestrationOptionDiagram } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/components/OrchestrationOptionDiagram';
import { OptionListItem } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OptionListItem';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { RadioFromFieldGroup } from '@/components/shared/components/NewContextForms/RadioFormFieldGroup';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { StatusTagType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

interface OptionListWithDetailProps {
  name: string;
  labelTextId: string;
  options: OptionListItem[];
  value: string;
  children?: ReactNode;
}

const useStyles = makeStyles((theme) => ({
  grid: ({ twoColumns }: StyleProps) => ({
    display: 'grid',
    gridTemplateColumns: twoColumns ? 'minmax(260px, 340px) 1fr' : '1fr',
    gap: 20,
    alignItems: 'start',
  }),
  label: { alignItems: 'flex-start', gap: theme.spacing(1) },
  // About five options; the next one peeks out so the list reads as scrollable.
  options: { maxHeight: 350, overflowY: 'auto', padding: 1 },
  restricted: ({ border, radius }: StyleProps) => ({
    outline: `1px dashed ${border}`,
    borderRadius: radius,
    padding: theme.spacing(0, 1),
  }),
}));

interface StyleProps {
  twoColumns: boolean;
  border: string;
  radius: number;
}

export const OptionListWithDetail = ({ name, labelTextId, options, value, children }: OptionListWithDetailProps) => {
  const { md } = useScreen();
  const { palette, customBorderRadius } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();
  const classes = useStyles({ twoColumns: md, border: palette.grey[400], radius: customBorderRadius.paper });
  const [previewValue, setPreviewValue] = useState<string | null>(null);

  useEffect(() => setPreviewValue(null), [value]);

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const list = listRef.current;
    const checked = list?.querySelector('input:checked')?.closest<HTMLElement>('[data-option-item]');
    const hiddenBelow =
      list && checked ? checked.getBoundingClientRect().bottom - list.getBoundingClientRect().bottom : 0;
    if (list && hiddenBelow > 0) {
      list.scrollTop += hiddenBelow + 8;
    }
  }, []);

  const previewHoveredOption = (event: MouseEvent<HTMLDivElement>) => {
    const input = (event.target as HTMLElement)
      .closest('[data-option-item]')
      ?.querySelector<HTMLInputElement>('input[type="radio"]');
    if (input) {
      setPreviewValue(input.value);
    }
  };

  const shown = options.find((option) => option.value === (previewValue ?? value)) ?? options[0];
  const ordered = [...options.filter((option) => !option.restricted), ...options.filter((option) => option.restricted)];
  const radioOptions = ordered.map((option) => ({
    value: option.value,
    label: getTextIdTranslation(option.titleTextId),
    disabled: option.disabled,
    element: (
      <Box py={1} className={option.restricted ? classes.restricted : undefined}>
        <Font variant="h5" color="title" display="block">
          <Text tId={option.titleTextId} />
          {option.restricted && (
            <StatusTag type={StatusTagType.INFO} labelTextId="LENDER_ORCHESTRATION_RESTRICTED" size="small" ml={1} />
          )}
        </Font>
        <Font variant="caption" color="textSecondary" display="block">
          <Text
            tId={option.disabled && option.disabledReasonTextId ? option.disabledReasonTextId : option.summaryTextId}
          />
        </Font>
      </Box>
    ),
  }));

  return (
    <div className={classes.grid}>
      <Box onMouseOver={previewHoveredOption} onMouseLeave={() => setPreviewValue(null)}>
        <Box className="fade-vertical-wrapper" pt={1}>
          <div ref={listRef} className={classes.options} data-option-list>
            <RadioFromFieldGroup
              name={name}
              label={getTextIdTranslation(labelTextId)}
              options={radioOptions}
              OptionContainer={OptionListItemContainer}
              radioProps={{ size: 'small' }}
              formControlLabelProps={{ className: classes.label, style: { display: 'flex' } }}
            />
          </div>
        </Box>
        {children}
      </Box>
      {shown && (
        <CustomPaper
          fill="paper"
          elevation={0}
          boxProps={{
            border: `1px solid ${palette.grey[200]}`,
            borderRadius: customBorderRadius.paper,
            marginTop: 2,
            marginBottom: 1,
          }}
        >
          <Box p={2} minHeight={'270px'}>
            <OrchestrationOptionDiagram field={shown.field} value={shown.value} />
            <Box mt={2}>
              <Font variant="h5" color="title" display="block">
                <Text tId={shown.titleTextId} />
              </Font>
              <Font variant="body2" display="block">
                <Text tId={shown.descriptionTextId} />
              </Font>
            </Box>
          </Box>
        </CustomPaper>
      )}
    </div>
  );
};
